import { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Progress,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  useColorModeValue,
  Button,
  Icon,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { MdSchool, MdPlayCircle, MdCheckCircle } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getEnrolledCourses, Course } from '../../services/university-service';
import { ANALYST_ROUTES, useRouteGuard } from '@bemap/utils';
import { PageHeading, AnimatedCard, containerVariants, cardVariants, Panel } from '@bemap/ui';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);

interface EnrolledCourse extends Course {
  status: 'enrolled' | 'in_progress' | 'completed' | 'dropped' | 'suspended';
  overallProgress: number;
  enrolledAt: string;
  course: Course;
  lastAccessedAt?: string;
  courseId: string;
}

export default function StudentDashboard() {
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCourses: 0,
    inProgress: 0,
    completed: 0,
    averageProgress: 0,
  });

  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useRouteGuard();
  const { t } = useTranslation();

  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    if (user?.id) {
      loadEnrolledCourses();
    }
  }, [user?.id]);

  const loadEnrolledCourses = async () => {
    try {
      setLoading(true);
      const response = await getEnrolledCourses(user!.id);

      if (response.statusCode === 200) {
        const courses = response.message as EnrolledCourse[];
        setEnrolledCourses(courses);

        // Calculate stats
        const totalCourses = courses.length;
        const inProgress = courses.filter(c => c.status === 'in_progress').length;
        const completed = courses.filter(c => c.status === 'completed').length;
        const averageProgress = totalCourses > 0
          ? Math.round(courses.reduce((sum, c) => sum + c.overallProgress, 0) / totalCourses)
          : 0;

        setStats({
          totalCourses,
          inProgress,
          completed,
          averageProgress,
        });
      } else {
        toast({
          title: t('university.courses.error_loading_courses'),
          description: response.message || t('general.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: t('university.courses.error_loading_courses'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'enrolled': return 'yellow';
      case 'dropped': return 'red';
      case 'suspended': return 'orange';
      default: return 'gray';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('university.students.status.completed');
      case 'in_progress': return t('university.students.status.in_progress');
      case 'enrolled': return t('university.students.status.enrolled');
      case 'dropped': return t('university.students.status.dropped');
      case 'suspended': return t('university.students.status.suspended');
      default: return t('university.students.unknown');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleCourseClick = (course: EnrolledCourse) => {
    const courseViewUrl = ANALYST_ROUTES.UNIVERSITY_COURSES_VIEW.replace(':courseId', course.courseId.toString());
    navigate(courseViewUrl);
  };

  const handleBrowseCourses = () => {
    navigate(ANALYST_ROUTES.UNIVERSITY_COURSES);
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      gap={4}
      align="stretch"
      alignItems="stretch"
    >
      <PageHeading title={t('university.studentDashboard.title')} actions={[
        {
          label: t('university.studentDashboard.actions.browse_courses'),
          onClick: handleBrowseCourses,
          icon: <MdPlayCircle />,
        }
      ]} />

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <AnimatedCard bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color="gray.500">{t('university.studentDashboard.stats.total_courses.label')}</StatLabel>
            <StatNumber color="blue.600">{stats.totalCourses}</StatNumber>
            <StatHelpText>{t('university.studentDashboard.stats.total_courses.help')}</StatHelpText>
          </Stat>
        </AnimatedCard>

        <AnimatedCard bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color="gray.500">{t('university.studentDashboard.stats.in_progress.label')}</StatLabel>
            <StatNumber color="orange.600">{stats.inProgress}</StatNumber>
            <StatHelpText>{t('university.studentDashboard.stats.in_progress.help')}</StatHelpText>
          </Stat>
        </AnimatedCard>

        <AnimatedCard bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color="gray.500">{t('university.studentDashboard.stats.completed.label')}</StatLabel>
            <StatNumber color="green.600">{stats.completed}</StatNumber>
            <StatHelpText>{t('university.studentDashboard.stats.completed.help')}</StatHelpText>
          </Stat>
        </AnimatedCard>

        <AnimatedCard bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color="gray.500">{t('university.studentDashboard.stats.average_progress.label')}</StatLabel>
            <StatNumber color="purple.600">{stats.averageProgress}%</StatNumber>
            <StatHelpText>{t('university.studentDashboard.stats.average_progress.help')}</StatHelpText>
          </Stat>
        </AnimatedCard>
      </SimpleGrid>

      {/* Enrolled Courses */}
      <Panel heading={t('university.studentDashboard.panel.my_courses')} transparent>
        {enrolledCourses.length === 0 ? (
          <AnimatedCard bg={cardBg} variants={cardVariants}>
            <Box textAlign="center" py={12}>
              <Icon as={MdSchool} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.500" fontSize="lg" mb={4}>
                {t('university.studentDashboard.empty.not_enrolled')}
              </Text>
              <Button colorScheme="blue" onClick={handleBrowseCourses}>
                {t('university.studentDashboard.empty.cta_browse')}
              </Button>
            </Box>
          </AnimatedCard>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {enrolledCourses.map((course) => (
              <AnimatedCard
                key={course.id}
                bg={cardBg}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-2px)',
                  shadow: 'lg',
                  borderColor: 'blue.300',
                }}
                onClick={() => handleCourseClick(course)}
                variants={cardVariants}
              >
                {course.thumbnailUrl && (
                  <Box
                    bgImage={`url(${course.thumbnailUrl})`}
                    bgSize="cover"
                    bgPosition="center"
                    height="200px"
                    borderRadius="md"
                    mb={4}
                    position="relative"
                  >
                    <Badge
                      position="absolute"
                      top={2}
                      right={2}
                      colorScheme={getStatusColor(course.status)}
                      size="sm"
                    >
                      {getStatusText(course.status)}
                    </Badge>
                  </Box>
                )}

                <VStack align="stretch" spacing={3}>
                  <Heading size="md" color={textColor} noOfLines={2}>
                    {course.course.title}
                  </Heading>

                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {course.course.description}
                  </Text>

                  <Text fontSize="sm" color="blue.600" fontWeight="medium">
                    {course.instructor}
                  </Text>

                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.500">
                        {t('university.courses.progress')}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {course.overallProgress}%
                      </Text>
                    </HStack>
                    <Progress
                      value={course.overallProgress}
                      colorScheme={course.overallProgress === 100 ? 'green' : 'blue'}
                      size="sm"
                      borderRadius="full"
                    />
                  </VStack>

                  <HStack justify="space-between" fontSize="sm" color="gray.500">
                    <Text>{t('university.students.enrolled_date')}:</Text>
                    <Text>{formatDate(course.enrolledAt)}</Text>
                  </HStack>

                  {course.lastAccessedAt && (
                    <HStack justify="space-between" fontSize="sm" color="gray.500">
                      <Text>{t('university.students.last_access')}:</Text>
                      <Text>{formatDate(course.lastAccessedAt)}</Text>
                    </HStack>
                  )}

                  {course.overallProgress === 100 && (
                    <HStack color="green.600" fontSize="sm">
                      <Icon as={MdCheckCircle} />
                      <Text fontWeight="medium">{t('university.studentDashboard.completed_banner')}</Text>
                    </HStack>
                  )}
                </VStack>
              </AnimatedCard>
            ))}
          </SimpleGrid>
        )}
      </Panel>
    </MotionBox>
  );
} 